import { useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import Select from "react-select";
import { Field, Form, Formik, ErrorMessage, setNestedObjectValues } from "formik";
import * as Yup from "yup";
import { useFilterStore } from "../../AssetVehicle/store/useFilterStore";
import { AllVehicleTypeDetails } from "../../../Apis/vehicleApi";
import { GetCollectionPointsTypes } from "../../../Apis/collectionPointsApi";
import { getTransferStationsByType } from "../../../Apis/transferStationApi";
import { useColonyStore } from "../../OrganizationColony/store/useColonyStore";
import { useRouteMasterStore } from "./RouteMasterStore/useRouteMasterStore";
import { toast } from "react-toastify";
import RouteDrawMapModal from "./RouteDrawMapModal";
import RouteStoppageMapModal from "./RouteStoppageMapModal";
import RouteModalCloseButton, {
  routeModalHeaderClassName,
} from "./RouteModalCloseButton";
import { getCurrentDate } from "../../../Common/helper/helpar";
import {
  buildCoordinateOptions,
  calculateRouteDistanceKm,
  createEmptyStoppage,
  getRouteCoordinatesSummary,
  resolvePlaceNameFromCoordinate,
  resolveRouteEndPlaceNames,
  toDateInputValue,
  unwrapMasterList,
} from "./routeMasterFormUtils";

const getTransferStationOptionId = (item, index) =>
  item?.transferStationId ?? item?.id ?? item?.transferStationID ?? index;

const getTransferStationOptionLabel = (item) =>
  item?.transferStationName ?? item?.name ?? item?.transferStation ?? "";

const getCollectionPointTypeLabel = (item) =>
  item?.collectionPointTypeName ?? item?.name ?? "";

const getCollectionPointTypeValue = (item) =>
  item?.collectionPointTypeId ?? item?.id ?? "";

const sectionHeaderStyle = {
  backgroundColor: "#EEF2FF",
  color: "#4F46E5",
  padding: "10px 14px",
  borderRadius: "6px",
  fontWeight: 600,
  fontSize: "0.95rem",
  marginBottom: "16px",
  marginTop: "8px",
};

const readOnlyFieldClass = "form-control bg-light text-muted";

const inlineIconsWrapStyle = {
  position: "relative",
  width: "100%",
};

const inlineIconsStyle = {
  position: "absolute",
  right: 10,
  top: "50%",
  transform: "translateY(-50%)",
  display: "flex",
  alignItems: "center",
  gap: 10,
  zIndex: 2,
};

const inlineIconBtnStyle = {
  border: "none",
  background: "transparent",
  padding: 0,
  color: "#64748b",
  fontSize: 15,
  lineHeight: 1,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
};

const InputWithInlineIcons = ({
  value,
  onChange,
  placeholder,
  readOnly = false,
  name,
  id,
  className = "",
  icons = [],
}) => (
  <div style={inlineIconsWrapStyle}>
    <input
      id={id}
      name={name}
      value={value ?? ""}
      onChange={onChange}
      readOnly={readOnly}
      placeholder={placeholder}
      className={`form-control ${className}`}
      style={{ paddingRight: Math.max(48, icons.length * 24 + 16) }}
    />
    {icons.length ? (
      <div style={inlineIconsStyle}>
        {icons.map((item) => (
          <button
            key={item.key}
            type="button"
            style={{
              ...inlineIconBtnStyle,
              color: item.color ?? "#64748b",
              cursor: item.disabled ? "not-allowed" : "pointer",
              opacity: item.disabled ? 0.45 : 1,
            }}
            onClick={item.onClick}
            title={item.title}
            aria-label={item.title}
            disabled={item.disabled}
          >
            <i className={item.iconClass} />
          </button>
        ))}
      </div>
    ) : null}
  </div>
);

const emptyFormValues = {
  routeDescription: "",
  applicableZoneId: "",
  applicableWardId: "",
  applicableColonyId: "",
  applicableLocation: "",
  applicableDurationToDate: "",
  applicableVehicleTypeId: "",
  routeType: "",
  routeCoordinates: [],
  startPointCoordinate: "",
  endPointCoordinate: "",
  totalDistanceKm: "",
  startPoint: "",
  endPoint: "",
  stoppages: [createEmptyStoppage(1)],
  routeApplicableFor: "",
  applicableMts: "",
  applicableRecyclingCenter: "",
  status: "true",
};

const validationSchema = Yup.object().shape({
  routeDescription: Yup.string().trim().required("Route Description is required"),
  applicableZoneId: Yup.string().required("Applicable Zone/Department is required"),
  applicableWardId: Yup.string().required("Applicable Ward is required"),
  applicableLocation: Yup.string().trim().required("Applicable Location is required"),
  applicableDurationToDate: Yup.string()
    .required("Applicable Duration To Date is required")
    .test(
      "not-before-today",
      "Applicable Duration To Date cannot be before today",
      (value) => !value || value >= getCurrentDate()
    ),
  routeType: Yup.string().required("Route Type is required"),
  routeCoordinates: Yup.array()
    .min(2, "Draw at least start and end route coordinates on the map")
    .required("Route Coordinates are required"),
  startPointCoordinate: Yup.string().required("Start Point Coordinate is required"),
  endPointCoordinate: Yup.string().required("End Point Coordinate is required"),
  totalDistanceKm: Yup.string().required("Total Distance is required"),
  startPoint: Yup.string().trim().required("Start Point is required"),
  endPoint: Yup.string().trim().required("End Point is required"),
  stoppages: Yup.array().of(
    Yup.object()
      .shape({
        name: Yup.string().trim(),
        latitude: Yup.string(),
        longitude: Yup.string(),
        stoppageCode: Yup.string(),
        locationName: Yup.string(),
      })
      .test("stoppage-required", "Stoppage name is required", function (stoppage) {
        if (!stoppage) return true;

        const hasData =
          stoppage.name?.trim() ||
          stoppage.latitude ||
          stoppage.longitude ||
          stoppage.stoppageCode?.trim() ||
          stoppage.locationName?.trim() ||
          stoppage.arrivalTime?.trim() ||
          stoppage.departureTime?.trim();

        if (!hasData) return true;

        if (!stoppage.name?.trim()) {
          return this.createError({
            path: `${this.path}.name`,
            message: "Stoppage name is required",
          });
        }

        return true;
      })
  ),
});

const markAllFieldsTouched = (formValues) => ({
  ...setNestedObjectValues(formValues, true),
  routeCoordinates: true,
});

const scrollToFirstValidationError = () => {
  requestAnimationFrame(() => {
    const firstError = document
      .querySelector("#route-master-modal-title")
      ?.closest(".modal-content")
      ?.querySelector(".required-message");

    firstError?.scrollIntoView({ behavior: "smooth", block: "center" });
  });
};

const buildEditFormValues = (editRow) => ({
  ...emptyFormValues,
  routeDescription: editRow?.routeDescription ?? editRow?.routeName ?? "",
  applicableZoneId: editRow?.zoneId?.toString() ?? "",
  applicableWardId:
    editRow?.circleId?.toString() ??
    editRow?.wardId?.toString() ??
    "",
  applicableColonyId: editRow?.colonyId?.toString() ?? "",
  applicableLocation: editRow?.applicableLocation ?? editRow?.location ?? "",
  applicableDurationToDate: toDateInputValue(
    editRow?.applicableDurationToDate ?? editRow?.toDate ?? ""
  ),
  applicableVehicleTypeId: editRow?.vehicleTypeId?.toString() ?? "",
  routeType: editRow?.routeType ?? "",
  routeCoordinates: editRow?.routeCoordinates ?? [],
  startPointCoordinate: editRow?.startPointCoordinate ?? "",
  endPointCoordinate: editRow?.endPointCoordinate ?? "",
  totalDistanceKm: editRow?.totalDistanceKm ?? editRow?.distance?.replace(" Km", "") ?? "",
  startPoint: editRow?.startPoint ?? "",
  endPoint: editRow?.endPoint ?? "",
  stoppages: editRow?.stoppages?.length
    ? editRow.stoppages
    : [createEmptyStoppage(1)],
  routeApplicableFor: editRow?.routeApplicableFor ?? "",
  applicableMts: editRow?.applicableMts ?? "",
  applicableRecyclingCenter: editRow?.applicableRecyclingCenter ?? "",
  status:
    editRow?.rawStatus === false || editRow?.status === "Inactive"
      ? "false"
      : "true",
});

const applyRoutePointsToForm = (points, setFieldValue) => {
  const coordinateOptions = buildCoordinateOptions(points);
  const startCoordinate = coordinateOptions[0] ?? "";
  const endCoordinate = coordinateOptions[coordinateOptions.length - 1] ?? "";
  const distance = calculateRouteDistanceKm(points);

  setFieldValue("routeCoordinates", points);
  setFieldValue("startPointCoordinate", startCoordinate);
  setFieldValue("endPointCoordinate", endCoordinate);
  setFieldValue("totalDistanceKm", distance);
};

const SectionHeader = ({ title, number, isFirst = false }) => (
  <h6
    className="mb-0"
    style={{
      ...sectionHeaderStyle,
      marginTop: isFirst ? 0 : sectionHeaderStyle.marginTop,
    }}
  >
    {number}. {title}
  </h6>
);

const AddRouteMaster = ({ showModal, setShowModal, editRow, isEditMode }) => {
  const { zones, circles, fetchFilterZones, fetchFilterCircles } = useFilterStore();
  const { colonies, fetchColonies } = useColonyStore();
  const { isSaving, saveRoute } = useRouteMasterStore();
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [routeTypes, setRouteTypes] = useState([]);
  const [routeApplicableForOptions, setRouteApplicableForOptions] = useState([]);
  const [applicableMtsOptions, setApplicableMtsOptions] = useState([]);
  const [recyclingCenterOptions, setRecyclingCenterOptions] = useState([]);
  const [showRouteDrawMap, setShowRouteDrawMap] = useState(false);
  const [activeStoppageIndex, setActiveStoppageIndex] = useState(null);
  const formikRef = useRef(null);
  const wasModalOpenRef = useRef(false);

  const delhiZones = useMemo(
    () =>
      (Array.isArray(zones) ? zones : []).filter(
        (zone) =>
          zone.isActive !== false
      ),
    [zones]
  );

  useEffect(() => {
    if (!showModal) return;

    fetchFilterZones();
    fetchColonies();

    const loadVehicleTypes = async () => {
      try {
        const response = await AllVehicleTypeDetails();
        const activeVehicleTypes = unwrapMasterList(response).filter(
          (vehicleType) => vehicleType.isActive === true
        );
        setVehicleTypes(activeVehicleTypes);
      } catch {
        setVehicleTypes([]);
      }
    };

    const loadRouteTypes = async () => {
      try {
        const response = await GetCollectionPointsTypes();
        setRouteTypes(unwrapMasterList(response));
      } catch {
        setRouteTypes([]);
      }
    };

    const loadTransferStationOptions = async () => {
      const loadByType = async (transferStationType) => {
        try {
          const response = await getTransferStationsByType(transferStationType);
          return unwrapMasterList(response);
        } catch {
          return [];
        }
      };

      const [routeApplicableFor, applicableMts, recyclingCenters] =
        await Promise.all([loadByType(1), loadByType(2), loadByType(3)]);

      setRouteApplicableForOptions(routeApplicableFor);
      setApplicableMtsOptions(applicableMts);
      setRecyclingCenterOptions(recyclingCenters);
    };

    loadVehicleTypes();
    loadRouteTypes();
    loadTransferStationOptions();
  }, [showModal, fetchFilterZones, fetchColonies]);

  useEffect(() => {
    const justOpened = showModal && !wasModalOpenRef.current;
    const justClosed = !showModal && wasModalOpenRef.current;

    if (justClosed) {
      formikRef.current?.resetForm({ values: emptyFormValues });
      setShowRouteDrawMap(false);
      setActiveStoppageIndex(null);
    }

    if (justOpened) {
      const nextValues =
        isEditMode && editRow ? buildEditFormValues(editRow) : emptyFormValues;
      formikRef.current?.resetForm({ values: nextValues });

      if (nextValues.applicableZoneId) {
        fetchFilterCircles(nextValues.applicableZoneId);
      }
    }

    wasModalOpenRef.current = showModal;
  }, [showModal, isEditMode, editRow, fetchFilterCircles]);

  useEffect(() => {
    // When modal opens and Delhi zones are loaded, autofill default zone
    if (!showModal || !delhiZones.length) return;
    const form = formikRef.current;
    if (!form) return;

    const currentZoneId = form.values?.applicableZoneId;
    if (currentZoneId) return;

    const preferred =
      delhiZones.find((z) => /delhi central/i.test(String(z.zoneName))) ||
      delhiZones[0];
    if (preferred) {
      form.setFieldValue("applicableZoneId", String(preferred.zoneId));
      fetchFilterCircles(String(preferred.zoneId));
    }
  }, [showModal, delhiZones, fetchFilterCircles]);

  const handleClose = () => setShowModal(false);

  const handleSubmit = async (values) => {
    const filledStoppages = values.stoppages.filter(
      (stoppage) =>
        stoppage.name?.trim() ||
        stoppage.latitude ||
        stoppage.longitude ||
        stoppage.stoppageCode?.trim() ||
        stoppage.locationName?.trim()
    );

    const payload = {
      routeName: values.routeDescription.trim(),
      routeCode: values.routeDescription.trim().replace(/\s+/g, "_").toUpperCase(),
      zoneId: values.applicableZoneId,
      wardId: values.applicableWardId,
      routeDescription: values.routeDescription.trim(),
      applicableZoneId: values.applicableZoneId,
      applicableWardId: values.applicableWardId,
      applicableColonyId: values.applicableColonyId,
      applicableLocation: values.applicableLocation.trim(),
      applicableDurationToDate: values.applicableDurationToDate,
      applicableVehicleTypeId: values.applicableVehicleTypeId,
      routeType: values.routeType,
      routeCoordinates: values.routeCoordinates,
      startPointCoordinate: values.startPointCoordinate,
      endPointCoordinate: values.endPointCoordinate,
      totalDistanceKm: values.totalDistanceKm,
      startPoint: values.startPoint.trim(),
      endPoint: values.endPoint.trim(),
      stoppages: filledStoppages,
      routeApplicableFor: values.routeApplicableFor,
      applicableMts: values.applicableMts,
      applicableRecyclingCenter: values.applicableRecyclingCenter,
    };

    if (isEditMode && editRow?.routeId) {
      payload.routeId = editRow.routeId;
    }

    const result = await saveRoute(payload, isEditMode);
    if (result?.success) {
      handleClose();
    }
  };

  return (
    <Formik
      innerRef={formikRef}
      initialValues={emptyFormValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ values, setFieldValue, submitForm, validateForm, setTouched }) => {
        const coordinateOptions = buildCoordinateOptions(values.routeCoordinates);
        const isMapModalOpen = showRouteDrawMap || activeStoppageIndex !== null;

        const filteredColonies = colonies.filter((colony) => {
          const matchesZone =
            !values.applicableZoneId ||
            String(colony.zoneId) === String(values.applicableZoneId);
          const matchesWard =
            !values.applicableWardId ||
            !colony.circleId ||
            String(colony.circleId) === String(values.applicableWardId);
          return matchesZone && matchesWard;
        });

        const wardOptions = Array.isArray(circles) ? circles : [];

        const handleRouteDrawSave = async (points) => {
          applyRoutePointsToForm(points, setFieldValue);

          const { startPoint, endPoint } = await resolveRouteEndPlaceNames(points);
          setFieldValue("startPoint", startPoint);
          setFieldValue("endPoint", endPoint);
        };

        const handleStartCoordinateChange = async (event) => {
          const coordinate = event.target.value;
          setFieldValue("startPointCoordinate", coordinate);

          if (!coordinate) {
            setFieldValue("startPoint", "");
            return;
          }

          const placeName = await resolvePlaceNameFromCoordinate(coordinate);
          setFieldValue("startPoint", placeName);
        };

        const handleEndCoordinateChange = async (event) => {
          const coordinate = event.target.value;
          setFieldValue("endPointCoordinate", coordinate);

          if (!coordinate) {
            setFieldValue("endPoint", "");
            return;
          }

          const placeName = await resolvePlaceNameFromCoordinate(coordinate);
          setFieldValue("endPoint", placeName);
        };

        const openStoppageMap = (index) => {
          if (
            !values.routeCoordinates?.length ||
            values.routeCoordinates.length < 2
          ) {
            toast.info(
              "Draw route coordinates in Geolocation Information first."
            );
            return;
          }

          setActiveStoppageIndex(index);
        };

        const addStoppage = () => {
          setFieldValue("stoppages", [
            ...values.stoppages,
            createEmptyStoppage(values.stoppages.length + 1),
          ]);
        };

        const removeStoppage = (index) => {
          if (values.stoppages.length === 1) return;
          setFieldValue(
            "stoppages",
            values.stoppages.filter((_, itemIndex) => itemIndex !== index)
          );
        };

        return (
          <>
            <Modal
              size="xl"
              show={showModal && !isMapModalOpen}
              onHide={handleClose}
              backdrop="static"
              centered
              contentClassName="overflow-visible"
              dialogClassName="overflow-visible"
              aria-labelledby="route-master-modal-title"
            >
              <Modal.Header className={routeModalHeaderClassName}>
                <Modal.Title id="route-master-modal-title">
                  {isEditMode ? "Edit Route" : "Add Route"}
                </Modal.Title>
                <RouteModalCloseButton onClick={handleClose} />
              </Modal.Header>
              <hr className="mt-4 mb-0" />
              <Modal.Body
                className="pt-3"
                style={{ maxHeight: "calc(100vh - 8rem)", overflowY: "auto" }}
              >
                <Form>
                  <SectionHeader number={1} title="Basic Information" isFirst />
                  <div className="row">
                    <div className="col-md-4 mb-3">
                      <label htmlFor="routeDescription" className="form-label">
                        Route Description <span className="text-danger">*</span>
                      </label>
                      <Field
                        id="routeDescription"
                        name="routeDescription"
                        className="form-control"
                        placeholder="Enter Route Description"
                      />
                      <ErrorMessage
                        className="required-message"
                        name="routeDescription"
                        component="div"
                      />
                    </div>

                    {/* <div className="col-md-4 mb-3">
                      <label htmlFor="applicableZoneId" className="form-label">
                        Applicable Zone/Department <span className="text-danger">*</span>
                      </label>
                      <Field
                        as="select"
                        id="applicableZoneId"
                        name="applicableZoneId"
                        className="form-select"
                        onChange={(event) => {
                          const zoneId = event.target.value;
                          setFieldValue("applicableZoneId", zoneId);
                          setFieldValue("applicableWardId", "");
                          setFieldValue("applicableColonyId", "");
                          if (zoneId) {
                            fetchFilterCircles(zoneId);
                          }
                        }}
                      >
                        {delhiZones.map((zone) => (
                          <option key={zone.zoneId} value={zone.zoneId}>
                            {zone.zoneName}
                          </option>
                        ))}
                      </Field>
                      <ErrorMessage
                        className="required-message"
                        name="applicableZoneId"
                        component="div"
                      />
                    </div> */}
                    <div className="col-md-4 mb-3">
                      <label htmlFor="applicableZoneId" className="form-label">
                        Applicable Zone/Department <span className="text-danger">*</span>
                      </label>

                      <Field name="applicableZoneId">
                        {({ field, form }) => (
                          <Select
                            inputId="applicableZoneId"
                            name="applicableZoneId"
                            placeholder="Select Zone/Department"
                            isSearchable
                            isClearable
                            options={delhiZones.map((zone) => ({
                              value: String(zone.zoneId),
                              label: zone.zoneName,
                            }))}
                            value={
                              delhiZones
                                .map((zone) => ({
                                  value: String(zone.zoneId),
                                  label: zone.zoneName,
                                }))
                                .find(
                                  (option) =>
                                    String(option.value) === String(field.value)
                                ) || null
                            }
                            onChange={(selectedOption) => {
                              const zoneId = selectedOption
                                ? selectedOption.value
                                : "";

                              form.setFieldValue("applicableZoneId", zoneId);
                              form.setFieldValue("applicableWardId", "");
                              form.setFieldValue("applicableColonyId", "");

                              if (zoneId) {
                                fetchFilterCircles(zoneId);
                              }
                            }}
                          />
                        )}
                      </Field>

                      <ErrorMessage
                        className="required-message"
                        name="applicableZoneId"
                        component="div"
                      />
                    </div>
                    {/* <div className="col-md-4 mb-3">
                      <label htmlFor="applicableWardId" className="form-label">
                        Applicable Ward <span className="text-danger">*</span>
                      </label>
                      <Field
                        as="select"
                        id="applicableWardId"
                        name="applicableWardId"
                        className="form-select"
                        disabled={!values.applicableZoneId}
                        onChange={(event) => {
                          setFieldValue("applicableWardId", event.target.value);
                          setFieldValue("applicableColonyId", "");
                        }}
                      >
                        <option value="">
                          {values.applicableZoneId
                            ? "Select Ward"
                            : "Select Zone first"}
                        </option>
                        {wardOptions.map((ward) => {
                          const wardId = ward.circleId ?? ward.wardId;
                          return (
                            <option key={wardId} value={wardId}>
                              {ward.circleName || ward.wardName || ward.wardNo}
                            </option>
                          );
                        })}
                      </Field>
                      <ErrorMessage
                        className="required-message"
                        name="applicableWardId"
                        component="div"
                      />
                    </div> */}

                    <div className="col-md-4 mb-3">
                      <label htmlFor="applicableWardId" className="form-label">
                        Applicable Ward <span className="text-danger">*</span>
                      </label>

                      <Field name="applicableWardId">
                        {({ field, form }) => (
                          <Select
                            inputId="applicableWardId"
                            name="applicableWardId"
                            placeholder={
                              values.applicableZoneId
                                ? "Select Ward"
                                : "Select Zone first"
                            }
                            isSearchable
                            isClearable
                            isDisabled={!values.applicableZoneId}
                            options={wardOptions.map((ward) => {
                              const wardId = ward.circleId ?? ward.wardId;

                              return {
                                value: String(wardId),
                                label: ward.circleName || ward.wardName || ward.wardNo,
                              };
                            })}
                            value={
                              wardOptions
                                .map((ward) => {
                                  const wardId = ward.circleId ?? ward.wardId;

                                  return {
                                    value: String(wardId),
                                    label: ward.circleName || ward.wardName || ward.wardNo,
                                  };
                                })
                                .find(
                                  (option) =>
                                    String(option.value) === String(field.value)
                                ) || null
                            }
                            onChange={(selectedOption) => {
                              form.setFieldValue(
                                "applicableWardId",
                                selectedOption ? selectedOption.value : ""
                              );
                              form.setFieldValue("applicableColonyId", "");
                            }}
                          />
                        )}
                      </Field>

                      <ErrorMessage
                        className="required-message"
                        name="applicableWardId"
                        component="div"
                      />
                    </div>

                    {/* <div className="col-md-4 mb-3">
                      <label htmlFor="applicableColonyId" className="form-label">
                        Applicable Colony
                      </label>
                      <Field
                        as="select"
                        id="applicableColonyId"
                        name="applicableColonyId"
                        className="form-select"
                        disabled={!values.applicableZoneId}
                      >
                        <option value="">Select Colony (optional)</option>
                        {filteredColonies.map((colony) => (
                          <option key={colony.colonyId} value={colony.colonyId}>
                            {colony.colonyName}
                          </option>
                        ))}
                      </Field>
                    </div> */}
                    <div className="col-md-4 mb-3">
                      <label htmlFor="applicableColonyId" className="form-label">
                        Applicable Colony
                      </label>

                      <Field name="applicableColonyId">
                        {({ field, form }) => (
                          <Select
                            inputId="applicableColonyId"
                            name="applicableColonyId"
                            placeholder="Select Colony (optional)"
                            isSearchable
                            isClearable
                            isDisabled={!values.applicableZoneId}
                            options={filteredColonies.map((colony) => ({
                              value: String(colony.colonyId),
                              label: colony.colonyName,
                            }))}
                            value={
                              filteredColonies
                                .map((colony) => ({
                                  value: String(colony.colonyId),
                                  label: colony.colonyName,
                                }))
                                .find(
                                  (option) =>
                                    String(option.value) === String(field.value)
                                ) || null
                            }
                            onChange={(selectedOption) => {
                              form.setFieldValue(
                                "applicableColonyId",
                                selectedOption ? selectedOption.value : ""
                              );
                            }}
                          />
                        )}
                      </Field>
                    </div>

                    <div className="col-md-4 mb-3">
                      <label htmlFor="applicableLocation" className="form-label">
                        Applicable Location <span className="text-danger">*</span>
                      </label>
                      <Field
                        id="applicableLocation"
                        name="applicableLocation"
                        className="form-control"
                        placeholder="Enter Location"
                      />
                      <ErrorMessage
                        className="required-message"
                        name="applicableLocation"
                        component="div"
                      />
                    </div>

                    <div className="col-md-4 mb-3">
                      <label htmlFor="applicableDurationToDate" className="form-label">
                        Applicable Duration To Date <span className="text-danger">*</span>
                      </label>
                      <div style={inlineIconsWrapStyle}>
                        <Field
                          type="date"
                          id="applicableDurationToDate"
                          name="applicableDurationToDate"
                          className="form-control"
                          min={getCurrentDate()}
                          style={{ color: values.applicableDurationToDate ? "#212529" : "#6c757d" }}
                        />
                        <span
                          style={{
                            ...inlineIconsStyle,
                            pointerEvents: "none",
                            color: "#64748b",
                          }}
                        >
                          <i className="fa-regular fa-calendar" />
                        </span>
                      </div>
                      <ErrorMessage
                        className="required-message"
                        name="applicableDurationToDate"
                        component="div"
                      />
                    </div>

                    {/* <div className="col-md-4 mb-3">
                      <label htmlFor="applicableVehicleTypeId" className="form-label">
                        Applicable Vehicle Type
                      </label>
                      <Field
                        as="select"
                        id="applicableVehicleTypeId"
                        name="applicableVehicleTypeId"
                        className="form-select"
                      >
                        <option value="">Select Vehicle Type</option>
                        {vehicleTypes.map((vehicleType) => (
                          <option
                            key={vehicleType.vehicleTypeId}
                            value={vehicleType.vehicleTypeId}
                          >
                            {vehicleType.vehicleTypeName}
                          </option>
                        ))}
                      </Field>
                    </div> */}

                    <div className="col-md-4 mb-3">
                      <label htmlFor="applicableVehicleTypeId" className="form-label">
                        Applicable Vehicle Type
                      </label>

                      <Field name="applicableVehicleTypeId">
                        {({ field, form }) => (
                          <Select
                            inputId="applicableVehicleTypeId"
                            name="applicableVehicleTypeId"
                            placeholder="Select Vehicle Type"
                            isSearchable
                            isClearable
                            options={vehicleTypes.map((vehicleType) => ({
                              value: String(vehicleType.vehicleTypeId),
                              label: vehicleType.vehicleTypeName,
                            }))}
                            value={
                              vehicleTypes
                                .map((vehicleType) => ({
                                  value: String(vehicleType.vehicleTypeId),
                                  label: vehicleType.vehicleTypeName,
                                }))
                                .find(
                                  (option) =>
                                    String(option.value) === String(field.value)
                                ) || null
                            }
                            onChange={(selectedOption) => {
                              form.setFieldValue(
                                "applicableVehicleTypeId",
                                selectedOption ? selectedOption.value : ""
                              );
                            }}
                          />
                        )}
                      </Field>
                    </div>

                    <div className="col-md-4 mb-3">
                      <label htmlFor="routeType" className="form-label">
                        Route Type <span className="text-danger">*</span>
                      </label>

                      <Field name="routeType">
                        {({ field, form }) => (
                          <Select
                            inputId="routeType"
                            name="routeType"
                            placeholder="Select Route Type"
                            isSearchable
                            isClearable
                            options={routeTypes.map((option, index) => ({
                              value: String(getCollectionPointTypeValue(option)),
                              label: getCollectionPointTypeLabel(option),
                            }))}
                            value={
                              routeTypes
                                .map((option, index) => ({
                                  value: String(getCollectionPointTypeValue(option)),
                                  label: getCollectionPointTypeLabel(option),
                                }))
                                .find(
                                  (option) =>
                                    String(option.value) === String(field.value)
                                ) || null
                            }
                            onChange={(selectedOption) => {
                              form.setFieldValue(
                                "routeType",
                                selectedOption ? selectedOption.value : ""
                              );
                            }}
                          />
                        )}
                      </Field>

                      <ErrorMessage
                        className="required-message"
                        name="routeType"
                        component="div"
                      />
                    </div>
                  </div>

                  <SectionHeader number={2} title="Geolocation Information" />
                  <div className="row">
                    <div className="col-md-4 mb-3">
                      <label htmlFor="routeCoordinatesSummary" className="form-label">
                        Route Coordinates <span className="text-danger">*</span>
                      </label>
                      <InputWithInlineIcons
                        id="routeCoordinatesSummary"
                        readOnly
                        value={
                          values.routeCoordinates.length
                            ? getRouteCoordinatesSummary(values.routeCoordinates)
                            : ""
                        }
                        placeholder="Draw Route Coordinates"
                        icons={[
                          {
                            key: "route-map",
                            iconClass: "fa-solid fa-location-dot",
                            title: "Draw route on map",
                            onClick: () => setShowRouteDrawMap(true),
                          },
                          {
                            key: "route-tag",
                            iconClass: "fa-solid fa-tag",
                            title: "Draw route on map",
                            onClick: () => setShowRouteDrawMap(true),
                          },
                        ]}
                      />
                      <ErrorMessage
                        className="required-message"
                        name="routeCoordinates"
                        component="div"
                      />
                    </div>

                    <div className="col-md-4 mb-3">
                      <label htmlFor="startPointCoordinate" className="form-label">
                        Start Point Coordinate <span className="text-danger">*</span>
                      </label>
                      <Select
                        inputId="startPointCoordinate"
                        name="startPointCoordinate"
                        placeholder={
                          coordinateOptions.length
                            ? "Select Start Point Coordinate"
                            : "Start Point Coordinate Will be Populated"
                        }
                        isSearchable
                        isClearable
                        options={coordinateOptions.map((option) => ({
                          value: String(option),
                          label: String(option),
                        }))}
                        value={
                          coordinateOptions
                            .map((option) => ({
                              value: String(option),
                              label: String(option),
                            }))
                            .find(
                              (option) =>
                                String(option.value) ===
                                String(values.startPointCoordinate)
                            ) || null
                        }
                        onChange={(selectedOption) => {
                          setFieldValue(
                            "startPointCoordinate",
                            selectedOption ? selectedOption.value : ""
                          );
                        }}
                      />
                      <ErrorMessage
                        className="required-message"
                        name="startPointCoordinate"
                        component="div"
                      />
                    </div>

                    {/* <div className="col-md-4 mb-3">
                      <label htmlFor="endPointCoordinate" className="form-label">
                        End Point Coordinate <span className="text-danger">*</span>
                      </label>
                      <Field
                        as="select"
                        id="endPointCoordinate"
                        name="endPointCoordinate"
                        className="form-select"
                        onChange={handleEndCoordinateChange}
                      >
                        <option value="">
                          {coordinateOptions.length
                            ? "Select End Point Coordinate"
                            : "End Point Coordinate Will be Populated"}
                        </option>
                        {coordinateOptions.map((option) => (
                          <option key={`end-${option}`} value={option}>
                            {option}
                          </option>
                        ))}
                      </Field>
                      <ErrorMessage
                        className="required-message"
                        name="endPointCoordinate"
                        component="div"
                      />
                    </div> */}

                    <div className="col-md-4 mb-3">
                      <label htmlFor="endPointCoordinate" className="form-label">
                        End Point Coordinate <span className="text-danger">*</span>
                      </label>

                      <Field name="endPointCoordinate">
                        {({ field, form }) => (
                          <Select
                            inputId="endPointCoordinate"
                            name="endPointCoordinate"
                            placeholder={
                              coordinateOptions.length
                                ? "Select End Point Coordinate"
                                : "End Point Coordinate Will be Populated"
                            }
                            isSearchable
                            isClearable
                            options={coordinateOptions.map((option) => ({
                              value: String(option),
                              label: String(option),
                            }))}
                            value={
                              coordinateOptions
                                .map((option) => ({
                                  value: String(option),
                                  label: String(option),
                                }))
                                .find(
                                  (option) =>
                                    String(option.value) === String(field.value)
                                ) || null
                            }
                            onChange={(selectedOption) => {
                              const value = selectedOption
                                ? selectedOption.value
                                : "";

                              form.setFieldValue("endPointCoordinate", value);
                              handleEndCoordinateChange({
                                target: {
                                  name: "endPointCoordinate",
                                  value: value,
                                },
                              });
                            }}
                          />
                        )}
                      </Field>

                      <ErrorMessage
                        className="required-message"
                        name="endPointCoordinate"
                        component="div"
                      />
                    </div>

                    <div className="col-md-4 mb-3">
                      <label htmlFor="totalDistanceKm" className="form-label">
                        Total Distance (Km) <span className="text-danger">*</span>
                      </label>
                      <Field
                        id="totalDistanceKm"
                        name="totalDistanceKm"
                        className={readOnlyFieldClass}
                        readOnly
                        placeholder="Total Distance Will be Calculated Automatically"
                      />
                      <ErrorMessage
                        className="required-message"
                        name="totalDistanceKm"
                        component="div"
                      />
                    </div>

                    <div className="col-md-4 mb-3">
                      <label htmlFor="startPoint" className="form-label">
                        Start Point <span className="text-danger">*</span>
                      </label>
                      <Field
                        id="startPoint"
                        name="startPoint"
                        className="form-control"
                        placeholder="Enter Start Point"
                      />
                      <ErrorMessage
                        className="required-message"
                        name="startPoint"
                        component="div"
                      />
                    </div>

                    <div className="col-md-4 mb-3">
                      <label htmlFor="endPoint" className="form-label">
                        End Point <span className="text-danger">*</span>
                      </label>
                      <Field
                        id="endPoint"
                        name="endPoint"
                        className="form-control"
                        placeholder="Enter End Point"
                      />
                      <ErrorMessage
                        className="required-message"
                        name="endPoint"
                        component="div"
                      />
                    </div>
                  </div>

                  <SectionHeader number={3} title="Stoppage Details" />
                  <div className="mb-3">
                    {values.stoppages.map((stoppage, index) => {
                      const stoppageIcons = [
                        {
                          key: `stoppage-map-${index}`,
                          iconClass: "fa-solid fa-location-dot",
                          title: "Mark stoppage on route map",
                          onClick: () => openStoppageMap(index),
                        },
                        {
                          key: `stoppage-tag-${index}`,
                          iconClass: "fa-solid fa-tag",
                          title: "Stoppage point details",
                          onClick: () => openStoppageMap(index),
                        },
                        {
                          key: `stoppage-minus-${index}`,
                          iconClass: "fa-solid fa-minus",
                          title: "Remove stoppage",
                          color: "#64748b",
                          disabled: values.stoppages.length === 1,
                          onClick: () => removeStoppage(index),
                        },
                      ];

                      if (index === values.stoppages.length - 1) {
                        stoppageIcons.push({
                          key: `stoppage-plus-${index}`,
                          iconClass: "fa-solid fa-plus",
                          title: "Add stoppage",
                          color: "#64748b",
                          onClick: addStoppage,
                        });
                      }

                      return (
                        <div className="mb-3" key={stoppage.id}>
                          <label className="form-label mb-1">
                            Stoppage {index + 1}
                          </label>
                          <Field name={`stoppages.${index}.name`}>
                            {({ field }) => (
                              <InputWithInlineIcons
                                {...field}
                                placeholder="SP HN 001, DARYAGUNJ"
                                icons={stoppageIcons}
                              />
                            )}
                          </Field>
                          <ErrorMessage
                            className="required-message"
                            name={`stoppages.${index}.name`}
                            component="div"
                          />
                        </div>
                      );
                    })}
                  </div>

                  <SectionHeader number={4} title="Other Information" />
                  {/* <div className="row">
                    <div className="col-md-4 mb-3">
                      <label htmlFor="routeApplicableFor" className="form-label">
                        Route Applicable For
                      </label>
                      <Field
                        as="select"
                        id="routeApplicableFor"
                        name="routeApplicableFor"
                        className="form-select"
                      >
                        <option value="">Select</option>
                        {routeApplicableForOptions.map((option, index) => (
                          <option
                            key={getTransferStationOptionId(option, index)}
                            value={getTransferStationOptionLabel(option)}
                          >
                            {getTransferStationOptionLabel(option)}
                          </option>
                        ))}
                      </Field>
                    </div> */}

                  <div className="row">
                    <div className="col-md-4 mb-3">
                      <label htmlFor="routeApplicableFor" className="form-label">
                        Route Applicable For
                      </label>

                      <Field name="routeApplicableFor">
                        {({ field, form }) => (
                          <Select
                            inputId="routeApplicableFor"
                            name="routeApplicableFor"
                            placeholder="Select"
                            isSearchable
                            isClearable
                            options={routeApplicableForOptions.map((option, index) => ({
                              value: getTransferStationOptionLabel(option),
                              label: getTransferStationOptionLabel(option),
                            }))}
                            value={
                              routeApplicableForOptions
                                .map((option, index) => ({
                                  value: getTransferStationOptionLabel(option),
                                  label: getTransferStationOptionLabel(option),
                                }))
                                .find(
                                  (option) =>
                                    String(option.value) === String(field.value)
                                ) || null
                            }
                            onChange={(selectedOption) => {
                              form.setFieldValue(
                                "routeApplicableFor",
                                selectedOption ? selectedOption.value : ""
                              );
                            }}
                          />
                        )}
                      </Field>
                    </div>

                    <div className="col-md-4 mb-3">
                      <label htmlFor="applicableMts" className="form-label">
                        Applicable MTS
                      </label>

                      <Field name="applicableMts">
                        {({ field, form }) => (
                          <Select
                            inputId="applicableMts"
                            name="applicableMts"
                            placeholder="Select"
                            isSearchable
                            isClearable
                            options={applicableMtsOptions.map((option, index) => ({
                              value: getTransferStationOptionLabel(option),
                              label: getTransferStationOptionLabel(option),
                            }))}
                            value={
                              applicableMtsOptions
                                .map((option, index) => ({
                                  value: getTransferStationOptionLabel(option),
                                  label: getTransferStationOptionLabel(option),
                                }))
                                .find(
                                  (option) =>
                                    String(option.value) === String(field.value)
                                ) || null
                            }
                            onChange={(selectedOption) => {
                              form.setFieldValue(
                                "applicableMts",
                                selectedOption ? selectedOption.value : ""
                              );
                            }}
                          />
                        )}
                      </Field>
                    </div>
                  </div>

                  {/* <div className="row">
                    <div className="col-md-4 mb-3">
                      <label htmlFor="applicableRecyclingCenter" className="form-label">
                        Applicable Recycling Center
                      </label>
                      <Field
                        as="select"
                        id="applicableRecyclingCenter"
                        name="applicableRecyclingCenter"
                        className="form-select"
                      >
                        <option value="">Select</option>
                        {recyclingCenterOptions.map((option, index) => (
                          <option
                            key={getTransferStationOptionId(option, index)}
                            value={getTransferStationOptionLabel(option)}
                          >
                            {getTransferStationOptionLabel(option)}
                          </option>
                        ))}
                      </Field>
                    </div>
                  </div> */}

                  <div className="row">
                    <div className="col-md-4 mb-3">
                      <label htmlFor="applicableRecyclingCenter" className="form-label">
                        Applicable Recycling Center
                      </label>

                      <Field name="applicableRecyclingCenter">
                        {({ field, form }) => (
                          <Select
                            inputId="applicableRecyclingCenter"
                            name="applicableRecyclingCenter"
                            placeholder="Select"
                            isSearchable
                            isClearable
                            options={recyclingCenterOptions.map((option, index) => ({
                              value: getTransferStationOptionLabel(option),
                              label: getTransferStationOptionLabel(option),
                            }))}
                            value={
                              recyclingCenterOptions
                                .map((option, index) => ({
                                  value: getTransferStationOptionLabel(option),
                                  label: getTransferStationOptionLabel(option),
                                }))
                                .find(
                                  (option) =>
                                    String(option.value) === String(field.value)
                                ) || null
                            }
                            onChange={(selectedOption) => {
                              form.setFieldValue(
                                "applicableRecyclingCenter",
                                selectedOption ? selectedOption.value : ""
                              );
                            }}
                          />
                        )}
                      </Field>
                    </div>
                  </div>
                  <hr className="mt-2 mb-3" />
                  <div className="d-flex justify-content-end gap-2 pb-1">
                    <button
                      type="button"
                      className="btn btn-outline-primary px-4"
                      onClick={handleClose}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary px-4"
                      onClick={async () => {
                        if (isSaving) return;

                        const errors = await validateForm();
                        if (errors && Object.keys(errors).length > 0) {
                          setTouched(markAllFieldsTouched(values));
                          scrollToFirstValidationError();
                          return;
                        }

                        await submitForm();
                      }}
                    >
                      {isSaving ? "Saving..." : isEditMode ? "Update" : "Save"}
                    </button>
                  </div>
                </Form>
              </Modal.Body>
            </Modal >

            <RouteDrawMapModal
              show={showRouteDrawMap}
              onHide={() => setShowRouteDrawMap(false)}
              initialPoints={values.routeCoordinates}
              onSave={handleRouteDrawSave}
            />

            <RouteStoppageMapModal
              show={activeStoppageIndex !== null}
              onHide={() => setActiveStoppageIndex(null)}
              stoppageIndex={activeStoppageIndex ?? 0}
              routeId={editRow?.routeId || 0}
              routePoints={values.routeCoordinates}
              saveToApi={false}
              existingStoppages={values.stoppages.filter(
                (item, itemIndex) =>
                  itemIndex !== activeStoppageIndex &&
                  item.latitude &&
                  item.longitude
              )}
              initialStoppage={
                activeStoppageIndex !== null
                  ? values.stoppages[activeStoppageIndex]
                  : {}
              }
              onSave={(stoppageData) => {
                if (activeStoppageIndex === null) return;

                setFieldValue(
                  `stoppages.${activeStoppageIndex}.name`,
                  stoppageData.name
                );
                setFieldValue(
                  `stoppages.${activeStoppageIndex}.stoppageCode`,
                  stoppageData.stoppageCode
                );
                setFieldValue(
                  `stoppages.${activeStoppageIndex}.locationName`,
                  stoppageData.locationName
                );
                setFieldValue(
                  `stoppages.${activeStoppageIndex}.latitude`,
                  stoppageData.latitude
                );
                setFieldValue(
                  `stoppages.${activeStoppageIndex}.longitude`,
                  stoppageData.longitude
                );
                setFieldValue(
                  `stoppages.${activeStoppageIndex}.arrivalTime`,
                  stoppageData.arrivalTime
                );
                setFieldValue(
                  `stoppages.${activeStoppageIndex}.departureTime`,
                  stoppageData.departureTime
                );
                if (stoppageData.stoppageId != null) {
                  setFieldValue(
                    `stoppages.${activeStoppageIndex}.stoppageId`,
                    stoppageData.stoppageId
                  );
                }
              }}
            />
          </>
        );
      }}
    </Formik >
  );
};

export default AddRouteMaster;
